<template>
  <div class="bg-white shadow rounded-lg dark:bg-neutral-800 dark:shadow-none relative">
    <!-- Notifications -->
    <NotificationPopup ref="notification" :type="notificationType" :title="notificationTitle" :text="notificationText" />
    <!-- Accredited State -->
    <div v-if="accredited_status == 'Accredited' || accredited_status == 'Manually Accredited' || accredited_status == 'Verified Accredited'" class="relative">
      <div class="absolute inset-0 custom-bg rounded-full"></div>
      <div class="relative p-5">
        <AppIconHeader>
          <div class="relative">
            <FolderOpenIcon class="size-8 text-operacolor" />
            <div class="absolute -bottom-1 -right-1 flex items-center justify-center size-5">
              <div class="relative size-5">
                <SolidCheckBadgeIcon class="size-5 text-white dark:text-green-500 absolute z-10 top-0 left-0" />
                <div class="bg-green-500 dark:bg-black size-3 absolute rounded-full top-1 left-1"></div>
              </div>
            </div>
          </div>
        </AppIconHeader>

        <div class="flex flex-col items-center justify-center mt-1.5">
          <div class="text-md font-semibold text-gray-900 dark:text-neutral-200 mb-3">Accreditation</div>
          <div class="flex items-center gap-1 mb-2 badge_green">
            {{ capitalizeFirstLetter(accredited_status) }}
            <CheckBadgeIcon
              class="w-4 h-4"
            />
          </div>
          <div class="text-sm text-gray-500 dark:text-neutral-400 font-light">You have been verified as an accredited</div>
          <div class="text-sm text-gray-500 dark:text-neutral-400 font-light">investor, no further action is required.</div>
          <div class="flex gap-5 mt-4">
            <AppButtonSecondary :text="showDocsText" :icon="ClockIcon" :icon-first="true" size="lg" color="less-dark" @click="showDocs = !showDocs" />
            <AppButtonPrimary
              text="Certificate"
              :icon="ArrowDownTrayIcon"
              :loading="loadingAiLetter"
              size="lg"
              @click="getAccreditationLetter()"
            />
          </div>
          <div v-if="showDocs" class="flex gap-5 w-full mt-4 bg-white dark:bg-neutral-800 max-w-3xl">
            <DragDrop
              class="w-full"
              text="or drag and drop"
              type="accreditation"
              :files="documents"
              :downloadable="true"
              accepted_text="PDF, JPG, or PNG (Max size 50 MB)"
              @changed-files="filesChanged"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Under Review State -->
    <div v-else-if="accredited_status == 'Under Review'" class="p-5">
      <div
        v-if="userStore.user.profiles_protected.role == 'partner_admin' || userStore.user.profiles_protected.role == 'super_admin'"
        class="absolute top-1 left-2 cursor-default"
      >
        <AppTooltip
          :content="`This account is being reviewed by our team. If you believe the accounts accreditation status is incorrect or if it has been more than 3 days since the last time documents were upload please contact us at support@operaalts.com. Additional Notes: ${props.account.accounts_protected?.ai_notes ?? 'None'}`"
          placement="top"
          max-width="200px"
        >
          <div class="rounded-full flex items-center gap-0.5 bg-gray-100 px-2 py-0.5">
            <div class="text-[10px]">Need Help</div>
            <QuestionMarkCircleIcon class="size-3 text-black" />
          </div>
        </AppTooltip>
      </div>
      <AppIconHeader>
        <div class="relative">
          <DocumentTextIcon class="size-8 text-operacolor" />
          <div class="absolute -bottom-1 -right-1 flex items-center justify-center size-5">
            <div class="relative size-5">
              <SolidClockIcon class="size-5 text-yellow-400 absolute z-10 top-0.5 left-0" />
              <div class="bg-white dark:bg-black size-3 absolute rounded-full top-1.5 left-1"></div>
            </div>
          </div>
        </div>
      </AppIconHeader>
      <div class="flex flex-col items-center justify-center mt-1.5">
        <div class="text-md font-semibold text-gray-900 dark:text-neutral-200 mb-3">Accreditation</div>
        <div class="flex items-center gap-1 mb-2 badge_blue">
          Under Review
          <ClockIcon class="w-4 h-4" />
        </div>
        <div class="text-sm text-gray-500 dark:text-neutral-400 font-light max-w-56 text-center">
          Your documents are under review. <b class="text-gray-800">No action is required</b> at this time.
        </div>
        <div class="flex gap-5 w-full mt-4 bg-white dark:bg-neutral-800 max-w-3xl">
          <DragDrop
            class="w-full"
            text="or drag and drop"
            type="accreditation"
            :files="documents"
            :downloadable="true"
            accepted_text="PDF, JPG, or PNG (Max size 50 MB)"
            @changed-files="filesChanged"
          />
        </div>
      </div>
    </div>

    <!-- Action Required State -->
    <div v-else-if="accredited_status == 'Action Required'" class="p-5">
      <div
        v-if="userStore.user.profiles_protected.role == 'partner_admin' || userStore.user.profiles_protected.role == 'super_admin'"
        class="absolute top-1 left-2 cursor-default"
      >
        <AppTooltip
          :content="`This account needs more information in order to be verified as an accredited account. If you think this is incorrect please contact us at support@operaalts.com. Additional Notes: ${props.account.accounts_protected?.ai_notes ?? 'None'}`"
          placement="top"
          max-width="200px"
        >
          <div class="rounded-full flex items-center gap-0.5 bg-gray-100 px-2 py-0.5">
            <div class="text-[10px]">Need Help</div>
            <QuestionMarkCircleIcon class="size-3 text-black" />
          </div>
        </AppTooltip>
      </div>
      <AppIconHeader>
        <div class="relative">
          <DocumentTextIcon class="size-8 text-operacolor" />
          <div class="absolute -bottom-1 -right-1 flex items-center justify-center size-5">
            <div class="relative size-5">
              <SolidExclamationTriangleIcon class="size-5 text-yellow-400 absolute z-10 top-0.5 left-0" />
              <div class="bg-white dark:bg-black size-3 absolute rounded-full top-1.5 left-1"></div>
            </div>
          </div>
        </div>
      </AppIconHeader>
      <div class="flex flex-col items-center justify-center mt-1.5">
        <div class="text-md font-semibold text-gray-900 dark:text-neutral-200 mb-3">Accreditation</div>
        <AppTooltip :content="`${props.account.accounts_protected?.ai_notes ?? ''}`" placement="right" max-width="200px">
          <div class="flex items-center gap-1 mb-2 badge_orange">
            Action Required
            <ExclamationTriangleIcon class="w-4 h-4" />
          </div>
        </AppTooltip>
        <div class="text-sm text-gray-500 dark:text-neutral-400 font-light max-w-72 text-center">
          The information submitted was insufficient to qualified you as an accredited investor. Please submit additional information.
          <span v-if="props.account.accounts_protected?.ai_notes">Added Note: {{ props.account.accounts_protected?.ai_notes }}</span>
        </div>
        <div class="flex gap-5 w-full mt-4 bg-white dark:bg-neutral-800 max-w-3xl">
          <DragDrop
            class="w-full"
            text="or drag and drop"
            type="accreditation"
            :files="documents"
            :downloadable="true"
            accepted_text="PDF, JPG, or PNG (Max size 50 MB)"
            @changed-files="filesChanged"
          />
        </div>
      </div>
    </div>

    <!-- Not Accredited State -->
    <div v-else class="p-5">
      <AppIconHeader>
        <div class="relative">
          <DocumentTextIcon class="size-8 text-operacolor" />
          <div class="absolute -bottom-1 -right-1 flex items-center justify-center size-5">
            <div class="relative size-5">
              <SolidXCircleIcon class="size-5 text-red-400 absolute z-10 top-0.5 left-0" />
              <div class="bg-white dark:bg-black size-3 absolute rounded-full top-1.5 left-1"></div>
            </div>
          </div>
        </div>
      </AppIconHeader>
      <div class="flex flex-col items-center justify-center mt-1.5">
        <div class="text-md font-semibold text-gray-900 dark:text-neutral-200 mb-3">Accreditation</div>
        <div class="flex items-center gap-1 mb-2 badge_red">
          Not Accredited
          <XCircleIcon class="w-4 h-4" />
        </div>
        <div class="text-sm text-gray-500 dark:text-neutral-400 font-light max-w-72 text-center">
          The information submitted was insufficient to qualified you as an accredited investor. Please submit additional information.
        </div>
        <div class="flex gap-5 w-full mt-4 bg-white dark:bg-neutral-800 max-w-3xl">
          <DragDrop
            class="w-full"
            text="or drag and drop"
            type="accreditation"
            :files="documents"
            :downloadable="true"
            accepted_text="PDF, JPG, or PNG (Max size 50 MB)"
            @changed-files="filesChanged"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// Essentials
import { ref, onMounted, computed } from "vue";

// Components
import NotificationPopup from "@/components/applicationui/AppNotification.vue";
import DragDrop from "@/components/applicationinputs/documents/DragDrop.vue";
import AppIconHeader from "@/components/applicationui/AppIconHeader.vue";
import AppTooltip from "@/components/applicationui/AppTooltip.vue";

// Libraries
import { capitalizeFirstLetter } from "@/helper/helper";
import {
  ArrowDownTrayIcon,
  CheckBadgeIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon,
  XCircleIcon,
} from "@heroicons/vue/24/outline";
import {
  DocumentTextIcon,
  FolderOpenIcon,
  XCircleIcon as SolidXCircleIcon,
  ClockIcon as SolidClockIcon,
  CheckBadgeIcon as SolidCheckBadgeIcon,
  ExclamationTriangleIcon as SolidExclamationTriangleIcon,
} from "@heroicons/vue/24/solid";
import AppButtonPrimary from "../applicationui/AppButtonPrimary.vue";
import AppButtonSecondary from "../applicationui/AppButtonSecondary.vue";

// Props
const props = defineProps({
  account: { type: Object, required: true, default: () => null }, //Expects account object
  user_id: { type: String, required: true, default: null }, //Expects the current user id
});

// Emits
const emit = defineEmits(["success"]);

// Stores
import { useUserStore } from "@/stores/user";
const userStore = useUserStore();
import { useAccountsStore } from "@/stores/accounts";
const accountsStore = useAccountsStore();

const accredited_status = ref(props?.account?.accounts_protected?.accredited_status);

const showDocs = ref(false);
const showDocsText = computed(() => {
  return showDocs.value ? "Hide Docs" : "View Docs";
});


// Get existing accreditation documents START
const documents = ref([]);
async function setAccreditationDocuments() {
  // Set default values
  let docs = [];
  let docs2 = [];
  // Get all accredited documents
  docs = await accountsStore.getAccreidationDocuments(props?.account?.user_id, props?.account?.id, props?.account?.tapi_account_id);
  if (props?.account?.user_id != props?.user_id) {
    docs2 = await accountsStore.getAccreidationDocuments(props?.user_id, props?.account?.id, props?.account?.tapi_account_id);
  }
  if (!docs && !docs2) {
    notify("failure", "Sorry", "We were unable to load your files. Please try again later, if the issue persists please contact tech support");
    return;
  }
  // Set documents
  documents.value = docs.concat(docs2);
}
// Get existing accreditation documents END

// Get accreditation letter START
const loadingAiLetter = ref(false);
async function getAccreditationLetter() {
  loadingAiLetter.value = true;
  const accreditationLetter = await accountsStore.getAccreditationLetter(props?.account?.id, props?.account?.tapi_account_id);
  if (!accreditationLetter) {
    notify(
      "failure",
      "Sorry",
      "We were unable to download your accreditation letter. Please try again later, if the issue persists please contact tech support",
    );
    loadingAiLetter.value = false;
    return;
  }
  window.open(accreditationLetter, "_blank");
  loadingAiLetter.value = false;
}
// Get accreditation letter END

// Handle file archival and addition START
async function filesChanged(dtNew, removedFileName) {
  if (removedFileName) {
    await handleFileRemoval(removedFileName);
  } else if (dtNew && dtNew.files.length > documents.value.length) {
    await handleUpload(dtNew);
  }
}
async function handleFileRemoval(removedFileName) {
  const fileToRemove = documents.value.find((doc) => doc.name === removedFileName);
  if (!fileToRemove) return;

  const timestamp = Date.now();
  const successful = await accountsStore.archiveAccreditationFile(
    `${fileToRemove.userFolder}/accredited/${fileToRemove.accountFolder}/${fileToRemove.name}`,
    `${fileToRemove.userFolder}/archived/${fileToRemove.accountFolder}/${timestamp}_${fileToRemove.name}`,
  );
  if (!successful) {
    notify("failure", "Sorry", "We were unable to archive your file. Please try again later, if the issue persists please contact tech support");
    return;
  }
  setAccreditationDocuments();
}
async function handleUpload(dtNew) {
  const newFiles = Array.from(dtNew.files).filter((newFile) => !documents.value.some((existingFile) => existingFile.name === newFile.name));

  // // Request AI Verification from TAPI if not requested
  if (!props.account.accounts_protected.ai_request_id) {
    const successful = await accountsStore.requestAiVerification(props.account.id, props.account.tapi_account_id);
    if (!successful) {
      notify(
        "failure",
        "Sorry",
        "We were unable to request your accreditation check. Please try again later, if the issue persists please contact tech support",
      );
      return;
    }
  }
  // If already requested AI Verification from TAPI update the request status to 'New Info Added'
  else {
    const successful = await accountsStore.updateAiRequest(props.account.id, props.account.accounts_protected.ai_request_id);
    if (!successful) {
      notify(
        "failure",
        "Sorry",
        "We were unable to update your accreditation status. Please try again later, if the issue persists please contact tech support",
      );
      return;
    }
  }

  const uploadPromises = [];

  for (const file of newFiles) {
    // Add the TAPI upload promise to the array
    uploadPromises.push(accountsStore.uploadAccreditationDocumentToTapi(props.account.id, props.account.tapi_account_id, file));
    // Add the Supabase upload promise to the array
    uploadPromises.push(accountsStore.uploadAccreditationDocumentToSupabase(props.user_id, props.account.id, file));
  }

  // Wait for all promises to resolve
  const results = await Promise.allSettled(uploadPromises);

  // Check if any of the API calls failed
  const anyFailed = results.some((result) => result.status != "fulfilled" || !result.value);
  // If any of the API calls failed, notify the user
  if (anyFailed) {
    notify("failure", "Sorry", "One or more files failed to upload. Please try again later, if the issue persists please contact tech support");
    return;
  }
  
  // Set accreditation status to 'Under Review'
  if (accredited_status.value != "Accredited") {
    accredited_status.value = "Under Review";
    emit("success");
  }

  // Reset all files (to include newly uploaded file)
  setAccreditationDocuments();
}
// Handle file archival and addition END

// Notification START
const notification = ref(null);
const notificationType = ref("success");
const notificationTitle = ref("Document Uploaded");
const notificationText = ref("Your document was uploaded successfully, refresh this page to access the new file");
const notify = (type, title, text) => {
  notificationType.value = type;
  notificationTitle.value = title;
  notificationText.value = text;
  notification.value.show();
};
// Notification END

// Mounted START
const loading = ref(true);
onMounted(async () => {
  // Set documents array
  await setAccreditationDocuments();
  // Ready
  loading.value = false;
});
</script>

<style scoped>
.custom-bg {
  box-shadow: 0 0 20px 20px white inset;
  background-color: transparent;
  background-image: url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f9fafb' fill-opacity='1'%3E%3Cpath d='M50 50c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10s-10-4.477-10-10 4.477-10 10-10zM10 10c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10S0 25.523 0 20s4.477-10 10-10zm10 8c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8zm40 40c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.dark .custom-bg {
  box-shadow: 0 0 20px 20px #262626 inset;
  background-color: transparent;
  background-image: url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23222222' fill-opacity='1'%3E%3Cpath d='M50 50c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10s-10-4.477-10-10 4.477-10 10-10zM10 10c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10S0 25.523 0 20s4.477-10 10-10zm10 8c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8zm40 40c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}
</style>
